// react
import { useMemo, useState } from 'react';
// libraries
import { Controller, useForm } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';

// api
import { usePostEnergyGroupEnergyTreeNodeMutation } from '@/api/generated/recalc/endpoints/v2/energyGroups';
import type { GetEnergyTreeNodeDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { DeleteButton, SubmitButton } from '@/components/ui/button';
import { RHFAutocomplete, RHFTextField } from '@/components/ui/form';

// feature-components
import { OpeningHoursEditor } from '@/features/energyxplorer/components/tree/OpeningHoursEditor';

// feature-utils
import { supportedUnitTypeIds } from '@/features/energyxplorer/utils/supportedUnitTypes';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';

// relative
import {
  toCreateFirstNodeFormValues,
  toPostEnergyTreeNodeDto,
} from './createFirstNodeForm.mappers';
import { createFirstNodeFormSchema } from './createFirstNodeForm.schema';
import type { SelectableUnit } from './createFirstNodeForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

interface DecoratedUnit extends SelectableUnit {
  notSupportedReason: string | null;
  isDisabled: boolean;
}

function decorateUnits(units: SelectableUnit[]): DecoratedUnit[] {
  return units.map((unit) => {
    const isSupported = supportedUnitTypeIds.includes(
      unit.UnitType?.UnitTypeId as number,
    );
    const isSensorUncalibrated =
      unit.UnitType?.UnitTypeId === 28 &&
      unit.UnitSensors?.[0]?.Sensor?.CalibrationType === 'Level1';

    let notSupportedReason: string | null = null;
    if (!isSupported) {
      notSupportedReason = 'Understøttes ikke';
    } else if (isSensorUncalibrated) {
      notSupportedReason = 'Sensor er ikke kalibreret (Level 1)';
    }

    return {
      ...unit,
      notSupportedReason,
      isDisabled: Boolean(notSupportedReason),
    };
  });
}

export default function CreateFirstNodeForm({
  energyGroupId,
  groupName,
  units,
  onSuccess,
  onDelete,
  isDeleting,
}: {
  energyGroupId: number;
  groupName?: string | null;
  units: SelectableUnit[];
  onSuccess: (node: GetEnergyTreeNodeDto) => void;
  onDelete: () => void;
  isDeleting?: boolean;
}) {
  const { t } = useTranslation(['forms', 'validation']);

  const [postEnergyTreeNode, { isLoading: isCreating }] =
    usePostEnergyGroupEnergyTreeNodeMutation();

  const [searchText, setSearchText] = useState('');

  const schema = createFirstNodeFormSchema(t);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toCreateFirstNodeFormValues(groupName),
    mode: 'onTouched',
  });

  const decoratedUnits = useMemo(() => decorateUnits(units), [units]);

  const onSubmit = handleSubmit(async (values) => {
    const result = await postEnergyTreeNode({
      groupId: energyGroupId,
      postEnergyTreeNodeDto: toPostEnergyTreeNodeDto(values),
    });

    if (!('error' in result)) {
      onSuccess(result.data);
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  });

  useAlertProvider({
    enabled: true,
    severity: 'info',
    message: t('createFirstNode.infoMessage', { ns: 'forms' }),
  });

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="energy-group-create-node-form"
    >
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                required
                fullWidth
                label={t('createFirstNode.name.label', { ns: 'forms' })}
                placeholder={t('createFirstNode.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createFirstNode.name.helperText', {
                  ns: 'forms',
                })}
                disabled={isCreating}
                data-testid="energy-group-node-name-input"
              />

              <RHFAutocomplete
                name="units"
                control={control}
                size="small"
                multiple
                helperText={t('createFirstNode.units.helperText', {
                  ns: 'forms',
                })}
                options={decoratedUnits}
                getOptionLabel={(unit) => `(${unit.UnitId}) ${unit.Name}`}
                isOptionEqualToValue={(option, value) =>
                  option.UnitId === value.UnitId
                }
                label={t('createFirstNode.units.label', { ns: 'forms' })}
                disabled={isCreating}
                data-testid="energy-group-unit-autocomplete-input"
                inputValue={searchText}
                onClose={() => setSearchText('')}
                onInputChange={(_event, newInputValue, reason) => {
                  if (reason !== 'reset') setSearchText(newInputValue);
                }}
                getOptionDisabled={(option) => Boolean(option.isDisabled)}
                filterSelectedOptions
                filterOptions={(options, { inputValue }) => {
                  const search = inputValue.trim().toLowerCase();
                  return options.filter((unit) => {
                    const nameMatch = unit.Name?.toLowerCase().includes(search);
                    const tagMatch = unit.Tags?.some((tag) =>
                      tag.Text?.toLowerCase().includes(search),
                    );
                    const sensorMatch = unit.UnitSensors?.some((s) =>
                      String(s.Sensor?.SensorId).includes(search),
                    );
                    const unitIdMatch = String(unit.UnitId).includes(search);
                    return nameMatch || tagMatch || sensorMatch || unitIdMatch;
                  });
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.UnitId}
                    data-testid={`energy-group-unit-option-${option.UnitId}`}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <Typography variant="body2">
                        ({option.UnitId}) {option.Name}
                      </Typography>
                      {option.Tags && option.Tags.length > 0 && (
                        <Box>
                          {option.Tags.map((tag) => (
                            <Chip
                              key={tag.TagId}
                              label={tag.Text}
                              size="small"
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                      {option.notSupportedReason && (
                        <Typography variant="caption" color="text.secondary">
                          {option.notSupportedReason}
                        </Typography>
                      )}
                    </Stack>
                  </li>
                )}
              />

              <Box>
                <Typography variant="body2" fontWeight={800} sx={{ mb: 1 }}>
                  {t('createFirstNode.openingHours.label', { ns: 'forms' })}
                </Typography>
                <Controller
                  name="openingHours"
                  control={control}
                  render={({ field }) => (
                    <OpeningHoursEditor
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t('createFirstNode.openingHours.helperText', {
                    ns: 'forms',
                  })}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="space-between" gap={1}>
          <DeleteButton
            onClick={onDelete}
            loading={isDeleting}
            disabled={isCreating}
            data-testid="energy-group-delete-button"
          >
            {isDeleting
              ? t('createFirstNode.deleting', { ns: 'forms' })
              : t('createFirstNode.delete', { ns: 'forms' })}
          </DeleteButton>
          <SubmitButton
            loading={isCreating}
            disabled={!isValid}
            data-testid="energy-group-create-node-button"
          >
            {isCreating
              ? t('createFirstNode.submitting', { ns: 'forms' })
              : t('createFirstNode.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
